import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { Toggle } from "@/components/Toggle/Toggle";

import { List } from "@/pages/Private/pages/List/schema/list";
import { useTrackerDestinationLists } from "@/pages/Private/pages/List/utils/useTrackerDestinationLists";
import {
	useGetPositiveReplyTrackingQuery,
	useUpdatePositiveReplyTrackingMutation,
} from "@/pages/Private/redux/profileTracker/positiveReplyTracking.api";

/**
 * "Track everyone who replies positively" — the central switch PREC-3417 asks for.
 *
 * A lead who answers "yes, interesting" is the strongest tracking candidate the product ever
 * sees, and until now nothing carried them into a tracker.
 *
 * The switch cannot be turned on without a destination: enabling it with nowhere to send them
 * would silently do nothing on every reply, which is exactly the class of bug this ticket is
 * about. A lead list may point at a different tracker; it cannot opt itself out.
 */
export const PositiveReplyTrackingSettings: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`profileTracker.positiveReplies.${key}`);

	const { data: settings } = useGetPositiveReplyTrackingQuery();
	const [updateSettings, { isLoading: isSaving }] = useUpdatePositiveReplyTrackingMutation();

	// Shared with every other "add to tracker" picker so the archived-list filter cannot drift.
	const { lists: trackerLists } = useTrackerDestinationLists({
		searchValue: "",
		includeJobChange: false,
	});

	const [enabled, setEnabled] = useState(false);
	const [trackerListId, setTrackerListId] = useState<number | null>(null);

	useEffect(() => {
		if (settings) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setEnabled(settings.enabled);
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setTrackerListId(settings.trackerListId);
		}
	}, [settings]);

	const options: AutoCompleteItem[] = (trackerLists ?? []).map((list: List) => ({
		title: list.name,
		id: list.id,
	}));

	const selected =
		options.find((option) => option.id === trackerListId) ??
		(settings?.trackerListId
			? { title: settings.trackerListName ?? "", id: settings.trackerListId }
			: undefined);

	const handleToggle = async () => {
		const next = !enabled;

		setEnabled(next);
		await updateSettings({ enabled: next });
	};

	const handleDestination = async (value?: AutoCompleteItem) => {
		const nextId = Number(value?.id) || null;

		setTrackerListId(nextId);
		await updateSettings({ trackerListId: nextId });
	};

	return (
		<div className="rounded-xl border border-border-light bg-white p-6">
			<h3 className="text-md font-bold">{ts("title")}</h3>
			<p className="mt-1 max-w-[680px] text-ssm text-text-meta">{ts("description")}</p>

			<div className="mt-4 flex items-center gap-3 rounded-[10px] border border-border-light bg-blue-action-200 p-3.5">
				<div className="flex-1">
					<div className="text-ssm font-semibold">{ts("toggleTitle")}</div>
					<div className="mt-0.5 text-xs text-text-meta">{ts("toggleDescription")}</div>
				</div>
				<Toggle
					disabled={isSaving}
					isChecked={enabled}
					name="positiveReplyTrackingEnabled"
					testId="positive-reply-tracking-toggle"
					onChange={handleToggle}
				/>
			</div>

			<div className="mt-4">
				<div className="mb-1.5 text-ssm font-semibold text-text-meta">{ts("destination")}</div>
				<Dropdown
					floating
					data={options}
					defaultValue={selected ?? { title: ts("destinationPlaceholder"), id: "" }}
					handleSelect={handleDestination}
				/>
				{enabled && !trackerListId && (
					<div
						className="mt-2 text-ssm font-semibold text-status-warning"
						data-testid="positive-reply-tracking-warning"
					>
						{ts("missingDestination")}
					</div>
				)}
				<p className="mt-2 text-xs text-text-meta">{ts("overrideHint")}</p>
			</div>
		</div>
	);
};
